import React from "react"
import Text from "@/components/common/Text"
import styled from "styled-components"
import { getCssSizeValue, getFontSize, getHtmlFromRedactorField, getMapKey, getPropSafely } from "@/utils/utils"
import { BREAKPOINTS, colors } from "@/styles/themeSettings"
import useGlobals from "@/hooks/useGlobals"
import GridContainer from "@/components/common/GridContainer"
import { useRouter } from "next/router"
import { formatToCardItem, getCardData } from "@/components/common/Cards"
import { getLinkData } from "@/components/common/Links/utils"
import GridCards from "@/components/common/Cards/Grid"
import Container from "@/components/common/Container"
import SimpleIconButton, { SIMPLE_ICON_BUTTON_ANIMATIONS, SIMPLE_ICON_BUTTON_CLASS_NAME } from "@/components/common/SimpleIconButton"
import useHover from "@/hooks/useHover"

const VARIANTS = {
	LIGHT: "light",
	DARK: "dark",
}

const SeeAlsoContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding-bottom: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	padding-top: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding-bottom: ${getCssSizeValue(96, BREAKPOINTS.DESKTOP)};
		padding-top: ${getCssSizeValue(96, BREAKPOINTS.DESKTOP)};
	}

	&.${VARIANTS.DARK} {
		background-color: ${({ theme }) => theme.colors.richBlack};
	}
`

const SeeAlsoContainerForNews = styled(Container)`
	position: relative;
	display: flex;
	flex-direction: column;
	row-gap: ${getCssSizeValue(60, BREAKPOINTS.DESKTOP)};
	padding-bottom: ${getCssSizeValue(96, BREAKPOINTS.DESKTOP)};
	padding-top: ${getCssSizeValue(80, BREAKPOINTS.DESKTOP)};

	&.${VARIANTS.DARK} {
		background-color: ${({ theme }) => theme.colors.richBlack};
	}
`

const Line = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.platinum};
	margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: ${getCssSizeValue(30, BREAKPOINTS.DESKTOP)};
	}
`

const StyledInnerWrapper = styled(GridContainer)`
	&.two-tiles {
		row-gap: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			row-gap: 0;
		}
	}

	.see-also-header {
		padding-bottom: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
		font-weight: ${({ theme }) => theme.fontWeight.medium};
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			padding-bottom: ${getCssSizeValue(64, BREAKPOINTS.DESKTOP)};
		}
	}
`

const StyledSingleBigTile = styled.div`
	border: 1px solid ${({ theme, bgColor }) => theme.colors[bgColor]};
	transition: background-color 0.2s ease-in-out;
	background-color: transparent;
	position: relative;
	cursor: pointer;
	padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} ${getCssSizeValue(16, BREAKPOINTS.MOBILE)}
		${getCssSizeValue(32, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(47, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(88, BREAKPOINTS.DESKTOP)}
			${getCssSizeValue(39, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(56, BREAKPOINTS.DESKTOP)};
		min-height: ${getCssSizeValue(240, BREAKPOINTS.DESKTOP)};
	}

	&:hover {
		transition: background-color 0.2s ease-in-out;
		background-color: ${({ theme }) => theme.colors.periwinkle};

		//svg {
		//    transform: rotate(0);
		//    transition: transform 0.3s ease-in-out;
		//}
	}

	p.big-tile-header {
		font-weight: ${({ theme }) => theme.fontWeight.semiBold};
		margin-bottom: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.DESKTOP)};
			font-weight: ${({ theme }) => theme.fontWeight.medium};
		}
	}

	.big-tile-description {
		line-height: ${getFontSize(21, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			line-height: ${getFontSize(32.4, BREAKPOINTS.TABLET)};
		}
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			line-height: ${getFontSize(32.4, BREAKPOINTS.DESKTOP)};
		}
	}

	.${SIMPLE_ICON_BUTTON_CLASS_NAME} {
		position: absolute;
		top: 0;
		right: 0;
	}
`

const GlobalLinksWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: repeat(4, 1fr);
	}
`

const SingleGlobalLink = styled.div`
	border: 1px solid ${({ theme, bgColor }) => theme.colors[bgColor]};
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	cursor: pointer;
	//border-bottom: none;
	transition:
		background-color 0.2s ease-in-out,
		color 0.2s ease-in-out,
		transform 0.2s ease-in-out;
	background-color: transparent;
	color: ${({ theme, bgColor }) => theme.colors[bgColor]};
	font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	line-height: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	font-weight: ${(props) => props.theme.fontWeight.semiBold};
	height: ${getCssSizeValue(50, BREAKPOINTS.MOBILE)};

	&:nth-child(1) {
		border-bottom: none;
		border-right: none;
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			border: 1px solid ${({ theme, bgColor }) => theme.colors[bgColor]};
			border-bottom: 0;
		}
	}

	&:nth-child(2) {
		border-bottom: none;
	}

	&:nth-child(3) {
		border-right: none;
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			border: 1px solid ${({ theme, bgColor }) => theme.colors[bgColor]};
			border-bottom: 0;
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		height: auto;
		font-size: ${getFontSize(15, BREAKPOINTS.DESKTOP)};
		line-height: ${getFontSize(19.5, BREAKPOINTS.DESKTOP)};
		border-bottom: none;
	}

	&.last {
		background-color: ${({ theme }) => theme.colors.chilliRed};
		color: ${({ theme }) => theme.colors.white};

		img.white {
			opacity: 1 !important;
		}

		img.dark {
			opacity: 0 !important;
		}

		&:hover {
			transition: background-color 0.2s ease-in-out;
			background-color: ${({ theme }) => theme.colors.pennRed} !important;
		}
	}

	padding-left: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding-left: ${getCssSizeValue(16, BREAKPOINTS.DESKTOP)};
	}

	&:last-child {
		border-bottom: 1px solid ${({ theme, bgColor }) => theme.colors[bgColor]};
	}

	.light {
		img.white {
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}

		img.black {
			opacity: 1;
			transition: opacity 0.2s ease-in-out;
		}
	}

	.dark {
		img.white {
			opacity: 1;
			transition: opacity 0.2s ease-in-out;
		}

		img.black {
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}
	}

	img {
		transition:
			background-color 0.2s ease-in-out,
			color 0.2s ease-in-out,
			transform 0.2s ease-in-out !important;
		transform: translate(-50%, -50%) scale(1);
	}

	&:hover {
		transition:
			background-color 0.2s ease-in-out,
			color 0.2s ease-in-out,
			transform 0.2s ease-in-out !important;
		background-color: ${({ theme }) => theme.colors.periwinkle};
		color: ${({ theme }) => theme.colors.white};

		img {
			transition:
				background-color 0.2s ease-in-out,
				color 0.2s ease-in-out,
				transform 0.2s ease-in-out !important;
			transform: translate(-50%, -50%) scale(1.1);
		}

		.light {
			img.white {
				opacity: 1;
				transition: opacity 0.2s ease-in-out;
			}

			img.black {
				opacity: 0;
				transition: opacity 0.2s ease-in-out;
			}
		}

		.dark {
			img.white {
				opacity: 1;
				transition: opacity 0.2s ease-in-out;
			}

			img.black {
				opacity: 0;
				transition: opacity 0.2s ease-in-out;
			}
		}
	}
`

const GlobalLinkIconWrapper = styled.div`
	min-width: ${getCssSizeValue(50, BREAKPOINTS.MOBILE)};
	// height: ${getCssSizeValue(60, BREAKPOINTS.DESKTOP)};
	height: 100%;
	position: relative;
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		min-width: ${getCssSizeValue(60, BREAKPOINTS.DESKTOP)};
		// height: ${getCssSizeValue(60, BREAKPOINTS.DESKTOP)};
		height: 100%;
	}

	&.light {
		border-left: 1px solid ${({ theme }) => theme.colors.richBlack};
	}

	&.dark {
		border-left: 1px solid ${({ theme }) => theme.colors.white};
	}
`

const IconLocal = styled.img`
	width: 24px;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%) scale(1);
`

export function getSeeAlsoInfo(data) {
	return data?.seeAlsoInfo || { header: "", variant: "" }
}

export function getSeeAlsoData(data) {
	const src = getPropSafely(data, "seeAlsoLinks", [])
	return src.map((item) => formatToCardItem(item))
}

const SingleBigTile = ({ item, color }) => {
	const router = useRouter()
	const [ref, hovering] = useHover()

	const cardHeader = item?.card?.header || ""
	const cardDescription = item?.card?.description || ""
	const { url } = getCardData(item, router.locale)

	return (
		<StyledSingleBigTile
			ref={ref}
			bgColor={color}
			onClick={() => router.push(url)}
			key={cardHeader}
			className="col-desktop-7 col-mobile-8"
		>
			<SimpleIconButton
				color={colors.white}
				bgColor={colors.chilliRed}
				borderColor={colors.chilliRed}
				rotation={135}
				animation={hovering ? SIMPLE_ICON_BUTTON_ANIMATIONS.ROTATE_45 : null}
			/>
			<Text color={color} className="big-tile-header typo-borna typo-borna-18">
				{cardHeader}
			</Text>
			<Text
				tag="div"
				color={color}
				className="big-tile-description typo-strawford typo-strawford-45"
				dangerouslySetInnerHTML={getHtmlFromRedactorField(cardDescription)}
			/>
		</StyledSingleBigTile>
	)
}

const SeeAlso = ({ data, header: hr = "", variant: vt = VARIANTS.LIGHT, date = false, horizontalCards, singleNewsPage = false }) => {
	const seeAlsoFixedLinks = useGlobals("seeAlsoFixedLinks")
	const globalLinks = Array.isArray(seeAlsoFixedLinks) ? seeAlsoFixedLinks : []
	const router = useRouter()
	if (data.length <= 0) return null
	const header = hr || ""
	const variant = vt || VARIANTS.LIGHT
	const color = variant === VARIANTS.DARK ? "white" : "richBlack"
	const maxTwoItemsData = data.slice(0, 2)
	return (
		<>
			{!singleNewsPage && (
				<SeeAlsoContainer className={`see-also ${variant}`}>
					<StyledInnerWrapper>
						<Line className="col-desktop-9 col-mobile-8" />
					</StyledInnerWrapper>
					<StyledInnerWrapper>
						{header.length > 0 && (
							<Text color={color} tag="h2" className="see-also-header col-desktop-6 col-mobile-8 typo-borna-66">
								{header}
							</Text>
						)}
					</StyledInnerWrapper>
					<StyledInnerWrapper className="two-tiles">
						{maxTwoItemsData.map((item, index) => {
							return <SingleBigTile key={getMapKey(item, index)} item={item} color={color} />
						})}
						<GlobalLinksWrapper className="col-desktop-2 col-mobile-8">
							{globalLinks.map((item, key) => {
								const id = item?.link?.id || ""
								const pageName = item?.link?.link?.pageName || ""
								const pictogramBlackUrl = item?.pictogramBlack?.url || ""
								const pictogramWhiteUrl = item?.pictogramWhite?.url || ""
								const { url } = getLinkData(item.link)
								return (
									<SingleGlobalLink
										isLast={key === globalLinks.length - 1}
										bgColor={color}
										onClick={() => router.push(url)}
										className={`${variant} ${key === globalLinks.length - 1 ? "last" : ""} typo-borna`}
										key={id}
									>
										{pageName}
										<GlobalLinkIconWrapper className={variant}>
											<IconLocal className="black" src={pictogramBlackUrl} alt="pictogram-black" />
											<IconLocal className="white" src={pictogramWhiteUrl} alt="pictogram-white" />
										</GlobalLinkIconWrapper>
									</SingleGlobalLink>
								)
							})}
						</GlobalLinksWrapper>
					</StyledInnerWrapper>
				</SeeAlsoContainer>
			)}
			{singleNewsPage && (
				<SeeAlsoContainerForNews className={`see-also ${variant}`}>
					{header.length > 0 && (
						<Text color={color} tag="h2" className="typo-borna-44">
							{header}
						</Text>
					)}
					<GridCards data={data} color={color} date={date} horizontalCards={horizontalCards} />
				</SeeAlsoContainerForNews>
			)}
		</>
	)
}

export default SeeAlso
